import { useEffect, useRef, useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6u1b076t0doov8ffmc0';

const contactInfo = [
  { icon: 'ri-map-pin-line', label: 'Address', value: 'Mocici 1b' },
  { icon: 'ri-building-line', label: 'City', value: 'Mocici' },
  { icon: 'ri-mail-send-line', label: 'Zip', value: '20213' },
  { icon: 'ri-earth-line', label: 'Country', value: 'Croatia' },
  { icon: 'ri-mail-line', label: 'Email', value: 'help@focusdoo.com' },
  { icon: 'ri-phone-line', label: 'Number', value: '+1 8148080527' },
];

export default function ContactSection() {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.querySelector('[name="message"]') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    try {
      const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <footer id="contact" className="bg-gray-950" ref={ref}>
      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-8 py-24">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Left: Info */}
          <div>
            <span className="inline-block border border-emerald-500/40 text-emerald-400 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-8">
              Contact
            </span>
            <h2 className="font-syne font-bold text-4xl text-white leading-tight mb-4">
              Let's Start a
              <br />
              <span className="text-emerald-400">Conversation</span>
            </h2>
            <p className="text-white/40 text-base leading-relaxed mb-12 max-w-sm">
              Have a question or want to know more about our products? Reach out — we're here and happy to help.
            </p>

            {/* Contact Details */}
            <ul className="flex flex-col gap-5">
              {contactInfo.map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500/10 shrink-0 mt-0.5">
                    <i className={`${item.icon} text-emerald-400 text-base`} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-white/30 text-xs font-semibold uppercase tracking-widest mb-0.5">
                      {item.label}
                    </span>
                    <span className="text-white/80 text-sm font-medium">{item.value}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Form */}
          <div>
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label className="text-white/50 text-xs font-semibold uppercase tracking-widest">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="John Smith"
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-emerald-500/60 transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-white/50 text-xs font-semibold uppercase tracking-widest">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-emerald-500/60 transition-colors"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-white/50 text-xs font-semibold uppercase tracking-widest">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  required
                  placeholder="How can we help you?"
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-emerald-500/60 transition-colors"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-white/50 text-xs font-semibold uppercase tracking-widest">
                  Message
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Tell us more about your inquiry..."
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-emerald-500/60 transition-colors resize-none"
                />
                <span className="text-white/20 text-xs text-right">Max 500 characters</span>
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="whitespace-nowrap mt-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-gray-950 font-semibold text-sm py-3.5 px-8 rounded-xl transition-all duration-300 cursor-pointer self-start"
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>

              {status === 'success' && (
                <p className="text-emerald-400 text-sm font-medium">
                  Thank you! Your message has been sent successfully.
                </p>
              )}
              {status === 'error' && (
                <p className="text-red-400 text-sm font-medium">
                  Something went wrong. Please check your message (max 500 characters) and try again.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/3b38fb7c-21b0-441e-9438-5f48c792e7e8.png"
              alt="FOCUS d.o.o."
              className="h-7 w-7 object-contain"
            />
            <span className="font-syne font-bold text-white text-sm">FOCUS d.o.o.</span>
          </div>
          <p className="text-white/20 text-xs text-center">
            &copy; {new Date().getFullYear()} FOCUS d.o.o. All rights reserved. Croatia.
          </p>
          <div className="flex items-center gap-2 text-white/20 text-xs">
            <i className="ri-apple-fill text-sm" />
            <span>iOS App Specialists</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
